import React, { useMemo } from "react"
import { Box, styled, Typography, List, useTheme } from "@mui/material"
import { useTranslation } from "providers/i18n"

import { CategoryFilterBar } from "components/CategoryFilterBar"
import { HorizontalScrollPane } from "components/HorizontalScrollPane"
import { EmptyData } from "components/EmptyData"
import { StarIcon } from "components/icons"

import { TouristPrivilegeListItem } from "../../components/domain/tourist-privilege"
import { Head } from "components/Head"
import { useTouristPrivilege } from "extensions/tourist-friendly/hooks/tourist-privilege/useTouristPrivilege"
import { getIcon } from "utils/icon"

const ListContainer = styled(Box)(({ theme }) => ({
  boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.1)",
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
}))

const OverflowContainer = styled(Box)(({ theme }) => ({
  marginLeft: theme.spacing(-3),
  marginRight: theme.spacing(-3),
  background: theme.palette.common.white,
}))

const BannerSection = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2, 5),
  backgroundColor: "#E95B9B",
  marginBottom: theme.spacing(4),
}))

const StickySection = styled(Box)(({ theme }) => ({
  position: "sticky",
  top: 0,
  zIndex: 1,
}))

const BannerImage = styled("img")(() => ({
  width: "100%",
  objectFit: "cover",
}))

export const TouristPrivilege = () => {
  const { t } = useTranslation()

  const {
    categoryFilter,
    categoryWithPrivilege,
    occupantWithPrivilege,
    handleChangeCategory,
    ref,
  } = useTouristPrivilege()

  const theme = useTheme()
  const emptyIcon = useMemo(
    () =>
      getIcon(theme?.assets?.empty?.touristPrivilege) || (
        <StarIcon sx={{ width: "2rem", height: "2rem" }} />
      ),
    [theme]
  )
  const { privilegeBanner } = theme.assets || {}

  return (
    <>
      <Head title={t("Tourist Privileges")} />
      <Box ref={ref}>
        {/* Hero Image */}
        {privilegeBanner?.url && (
          <BannerSection>
            <BannerImage src={privilegeBanner?.url} />
          </BannerSection>
        )}
        {/* Category Filter Bar */}
        <StickySection>
          <OverflowContainer>
            <HorizontalScrollPane>
              <CategoryFilterBar
                categories={categoryWithPrivilege}
                activeCategory={categoryFilter}
                onChange={handleChangeCategory}
                sx={{ pt: 2, pb: 3 }}
              />
            </HorizontalScrollPane>
          </OverflowContainer>
        </StickySection>
        <Typography sx={{ mb: 2, fontWeight: "bold" }}>{t("Shop")}</Typography>
        {/* Occupant List */}
        <ListContainer>
          <List>
            {occupantWithPrivilege && occupantWithPrivilege.length > 0 ? (
              occupantWithPrivilege.map((occupant) => (
                <TouristPrivilegeListItem
                  key={`tourist-privilege-item-${occupant.id}`}
                  occupant={occupant}
                />
              ))
            ) : (
              <EmptyData
                icon={emptyIcon}
                primary={t("Coming soon")}
                secondary={t("Stay tuned for the update")}
              />
            )}
          </List>
        </ListContainer>
      </Box>
    </>
  )
}
